import apiClient from '../client';
import type { TeamAPIV2, TeamSwapOptionAPIV2 } from '../../types';

// NOTE: `baseURL` override is a temporary workaround until the apiClient is
// refactored to use `/api` as base and embed the version in the path.
const V2_BASE = '/api/v2';

/**
 * The tender's proposed team, or a statement of why there is none
 * (team_proposal_spec §3–§7; see redesign-docs/team_api_contract.md).
 *
 * `outcome` is always present and is the first thing to branch on: a tender
 * asking for capacity rather than a team, and a single-position tender, both
 * yield no seats — but for different reasons the recruiter is entitled to hear.
 * A 404 means the tender is unknown and nothing else, so `null` here is a
 * genuinely missing tender, never "no team".
 *
 * The first call seeds the stored selection; every later call returns the
 * recruiter's team rather than a freshly computed proposal (§7).
 */
export async function fetchTeamV2(tenderId: string): Promise<TeamAPIV2 | null> {
  const response = await apiClient.get<TeamAPIV2>(`/tenders/${tenderId}/team`, {
    baseURL: V2_BASE,
    validateStatus: (status) => status === 200 || status === 404,
  });
  if (response.status === 404) return null;
  return response.data;
}

/**
 * The team lens on a position's swap list (§6): per candidate, what they would
 * cover, how scarce that coverage is, and what seating them gains, loses and
 * costs in band.
 *
 * Annotations keyed by employee, laid over the position's ranked list from
 * `fetchCandidatesV2` — the swap list stays that list, not a second ranking.
 * A candidate with no entry cannot be seated.
 */
export async function fetchSwapOptionsV2(
  tenderId: string,
  positionOrdinal: number,
): Promise<TeamSwapOptionAPIV2[]> {
  const response = await apiClient.get<TeamSwapOptionAPIV2[]>(
    `/tenders/${tenderId}/team/${positionOrdinal}/options`,
    { baseURL: V2_BASE, validateStatus: (status) => status === 200 || status === 404 },
  );
  if (response.status === 404) return [];
  return Array.isArray(response.data) ? response.data : [];
}

/** How a seat write resolved — mirrors the endpoint's status codes. */
export type SeatResultV2 =
  | 'SEATED'
  | 'HELD_ELSEWHERE'
  | 'POSITION_FULL'
  | 'INELIGIBLE'
  | 'NOT_FOUND';

/**
 * Seat a candidate, or move them here from the seat that holds them.
 *
 * Seat uniqueness is structural, not evaluative: a plain seat REFUSES a
 * candidate already held (`HELD_ELSEWHERE`) so the surface can render them inert
 * and say which seat holds them. Do not auto-retry as a move — the emptying
 * lands on a seat the recruiter is not looking at, so it has to be asked for
 * (§6).
 *
 * A role holds as many people as the tender asked for. Seating into a full role
 * that holds SEVERAL people yields `POSITION_FULL`: with two Senior Consultants
 * seated, "seat this third one" does not say which of the two leaves. A full
 * role holding exactly one person still replaces, which is unambiguous. The
 * caller resolves `POSITION_FULL` by unseating somebody, never by guessing.
 */
export async function seatCandidateV2(
  tenderId: string,
  positionOrdinal: number,
  employeeId: string,
  move = false,
  heldElsewhere = false,
): Promise<SeatResultV2> {
  const response = await apiClient.put(
    `/tenders/${tenderId}/team/${positionOrdinal}`,
    { employee_id: employeeId, move },
    {
      baseURL: V2_BASE,
      validateStatus: (status) =>
        status === 204 || status === 409 || status === 422 || status === 404,
    },
  );
  if (response.status === 204) return 'SEATED';
  // 409 covers both conflicts. The caller knows which it is: it already knows
  // whether this candidate is held elsewhere, from the team it rendered.
  if (response.status === 409) return heldElsewhere ? 'HELD_ELSEWHERE' : 'POSITION_FULL';
  if (response.status === 422) return 'INELIGIBLE';
  return 'NOT_FOUND';
}

/**
 * Empty a named person's seat (§6). First-class rather than half of a move: a
 * chair left deliberately empty is a different fact from one nothing could
 * fill, and the feasibility read distinguishes them.
 *
 * The person is named because a role can hold several of them — a tender asking
 * for two Senior Consultants gives that role two interchangeable chairs, so an
 * ordinal alone would not say whose seat is being emptied.
 */
export async function unseatV2(
  tenderId: string,
  positionOrdinal: number,
  employeeId: string,
): Promise<boolean> {
  const response = await apiClient.delete(
    `/tenders/${tenderId}/team/${positionOrdinal}/${employeeId}`,
    { baseURL: V2_BASE, validateStatus: (status) => status === 204 || status === 404 },
  );
  return response.status === 204;
}
